import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { createJob } from "../models/models";
import { hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";
import { buildInvoicePdfDoc } from "../utils/invoicePdf";
import {
  exportPdf,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";

const STATUS_ORDER = [
  "unassigned",
  "assigned",
  "in_progress",
  "closed",
  "canceled",
];
const STATUS_LABEL = {
  requested: "Requested",
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Canceled",
};

/**
 * Facility job boards (Phase A). The facility's OWN maintenance work —
 * separate from the contractor's jobs. Grouped by building/location:
 * Executive sees every location (the master board); Facilities Manager /
 * Maintenance see only the location(s) assigned to them (the `customers`
 * tree passed in is already role-filtered). Reuses the org-scoped jobs
 * subcollection + addJob/updateJob; no costing/tickets/POs here.
 */
export const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Facility-side invoice print. Now jsPDF-based (was an HTML print window)
// so it matches the contractor's Summary bill layout — same shared builder
// in src/utils/invoicePdf.js. Summary-only by design: description +
// subtotal + tax / fee lines + total. Itemized copies are sent manually
// by the contractor.
//
// Args:
//   invoice  — publicInvoice mirror doc
//   building — the facility's location object (street, city, state, zip,
//              name); pass the full object so the helper can render the
//              service-location address block
//   customer — facility customer object (for Bill To name + billing addr).
//              Passing the full customer means we can include billing
//              address; if all you have is a name, pass { name } and it
//              works fine.
export const printInvoice = async (invoice, building, customer) => {
  // Tax-exempt status comes from the publicInvoice mirror (taxExempt +
  // taxExemptCert fields), which the mirrorInvoice Cloud Function pulls
  // from the contractor's account doc at finalize time. We don't trust
  // the local facility-side customer doc for this because the facility's
  // copy is separate from the contractor's.
  const doc = await buildInvoicePdfDoc({
    orgName: invoice.contractorOrgName || "",
    orgAddress: {},
    logoUrl: invoice.contractorLogoUrl || "",
    invoiceNumber: invoice.invoiceNumber || "",
    jobNumber: invoice.jobNumber || "",
    jobTitle: invoice.title || "",
    date: invoice.finalizedAt ? new Date(invoice.finalizedAt) : new Date(),
    customerName: customer?.name || "—",
    customerBilling: {
      street: customer?.billingStreet,
      city: customer?.billingCity,
      state: customer?.billingState,
      zip: customer?.billingZip,
    },
    locationName: building?.name || "—",
    locationAddress: {
      street: building?.street,
      suite: building?.suite,
      building: building?.building,
      city: building?.city,
      state: building?.state,
      zip: building?.zip,
    },
    workDescription: invoice.workDescription || "",
    subtotal: invoice.subtotal || 0,
    taxes: invoice.taxes || [],
    grandTotal: invoice.grandTotal || 0,
    taxExempt: !!invoice.taxExempt,
    taxExemptCert: invoice.taxExemptCert || "",
    // Multi-bill statement: priorBills is the trimmed list of bills
    // issued before this one (mirrored by mirrorInvoice). When present
    // the helper renders the prominent boxed "TOTAL DUE NOW" + the
    // "Bills on this job" statement table. priorBilled stays as a
    // legacy-fallback for older mirror docs that don't carry priorBills.
    priorBills: invoice.priorBills || [],
    priorBilled: invoice.priorBilled || 0,
    finalizedAt: invoice.finalizedAt || "",
  });

  const dateSlug = new Date().toISOString().slice(0, 10);
  const idSlug =
    invoice.invoiceNumber || invoice.jobNumber || invoice.jobId || "invoice";
  const filename = `Invoice_${idSlug}_${dateSlug}.pdf`;

  if (shouldShowPdfChooser()) {
    // Web: open the PDF in a new tab w/ auto-print. Same one-button UX
    // the facility had before (no extra chooser modal — the browser's
    // print dialog covers Save-as-PDF via its destination menu).
    printPdf(doc);
  } else {
    // Native (Capacitor): hand to the OS share sheet so the facility can
    // save / email / print from the field.
    await exportPdf(doc, filename);
  }
};

export default function FacilityJobsPage({
  jobsRaw = [],
  publicJobsRaw = [],
  publicTicketsRaw = [],
  publicInvoicesRaw = [],
  customers = [],
  actions,
  role,
  capabilities,
  user,
  orgId,
  onSelectJob,
  onBack,
}) {
  const canCreate = hasCapability(role, capabilities, "createJobs");
  const canManageJobs = hasCapability(role, capabilities, "editJobs");

  const buildings = customers.flatMap((c) =>
    (c.locations || []).map((b) => ({ building: b, account: c }))
  );

  // Org users for staff assignment (executive/manager can read them).
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId || !canManageJobs) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snap) => setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, [orgId, canManageJobs]);

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [equipmentIds, setEquipmentIds] = useState([]);
  // "Other" flags work that isn't tied to a tracked equipment record
  // (lighting, plumbing, general repairs). Details about what live in
  // the Details textarea above.
  const [otherChecked, setOtherChecked] = useState(false);

  const selectedBuilding = buildings.find((b) => b.building.id === buildingId);
  const equipmentForBuilding = selectedBuilding?.building.equipment || [];

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setBuildingId("");
    setEquipmentIds([]);
    setOtherChecked(false);
  };

  const handleCreate = async () => {
    if (!title || !buildingId) return;
    const account = selectedBuilding?.account;
    const job = {
      ...createJob(title),
      description,
      customerId: account?.id || "",
      locationId: buildingId,
      equipmentIds,
      otherEquipment: otherChecked,
      createdBy: user?.email || "unknown",
    };
    await actions.addJob(job);
    resetForm();
    setShowCreate(false);
  };

  const staffName = (uid) =>
    orgUsers.find((u) => u.id === uid)?.email || "staff";

  // Signature image url or typed name, whichever is present.
  const sigName = (sig) => sig?.name || sig?.typedName || "signed";

  // Contractor-side job mirror — read-only, no costing. We surface number /
  // title / status / who-requested + the SIGNED service tickets (proof of
  // work, no cost), so the facility can see open work on their buildings
  // without seeing cost or margin.
  const renderContractorJob = (job) => {
    const tickets = publicTicketsRaw
      .filter((t) => t.jobId === job.id)
      .sort((a, b) => (b.workDate || "").localeCompare(a.workDate || ""));
    const invoice = publicInvoicesRaw.find((i) => i.jobId === job.id);
    return (
    <div
      key={job.id}
      style={{ borderTop: "1px solid #eee", padding: "6px 0", fontSize: "13px" }}
    >
      <strong>
        {job.jobNumber ? `${job.jobNumber} — ` : ""}
        {job.title}
      </strong>{" "}
      <span
        style={{
          fontSize: "11px",
          padding: "1px 6px",
          background: "#dde7f4",
          borderRadius: "4px",
          marginLeft: "4px",
        }}
      >
        Contractor
      </span>{" "}
      <span
        style={{
          fontSize: "11px",
          padding: "1px 6px",
          background: "#e0e0e0",
          borderRadius: "4px",
        }}
      >
        {STATUS_LABEL[job.status] || job.status}
      </span>
      {job.billFinalized && (
        <span
          style={{
            fontSize: "11px",
            padding: "1px 6px",
            background: "#c9efc9",
            borderRadius: "4px",
            marginLeft: "4px",
          }}
        >
          Bill finalized
        </span>
      )}
      {job.description && (
        <>
          <br />
          <span style={{ fontSize: "12px", color: "#555", marginLeft: "0" }}>
            {job.description}
          </span>
        </>
      )}
      {job.requestedByName && (
        <>
          <br />
          <span style={{ fontSize: "11px", color: "#666" }}>
            Requested by {job.requestedByName}
          </span>
        </>
      )}
      {tickets.length > 0 && (
        <div style={{ marginTop: "6px", marginLeft: "8px" }}>
          <strong style={{ fontSize: "11px", color: "#666" }}>
            Signed tickets ({tickets.length})
          </strong>
          {tickets.map((t) => (
            <div
              key={t.id}
              style={{ fontSize: "12px", color: "#444", marginTop: "2px" }}
            >
              • {t.workDate || "—"}
              {t.summary ? ` — ${t.summary}` : ""}
              {t.customerSignature && (
                <span style={{ fontSize: "11px", color: "#666" }}>
                  {" "}
                  (signed by {sigName(t.customerSignature)})
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {invoice && (
        <div style={{ marginTop: "6px", marginLeft: "8px" }}>
          <button
            style={styles.button}
            onClick={() => {
              const acct = customers.find((c) => c.id === invoice.customerId);
              const bld = (acct?.locations || []).find(
                (b) => b.id === invoice.locationId
              );
              printInvoice(invoice, bld, acct);
            }}
          >
            View / Print Invoice ({money(invoice.grandTotal)})
          </button>
        </div>
      )}
    </div>
    );
  };

  const renderJob = (job) => {
    return (
      <div
        key={job.id}
        style={{
          borderTop: "1px solid #eee",
          padding: "8px 0",
          fontSize: "13px",
          cursor: "pointer",
        }}
        onClick={() => onSelectJob && onSelectJob(job)}
      >
        <strong>
          {job.jobNumber ? `${job.jobNumber} — ` : ""}
          {job.title}
        </strong>{" "}
        <span
          style={{
            fontSize: "11px",
            padding: "1px 6px",
            background: "#e0e0e0",
            borderRadius: "4px",
          }}
        >
          {STATUS_LABEL[job.status] || job.status}
        </span>
        <br />
        <span style={{ fontSize: "12px", color: "#666", marginLeft: "14px" }}>
          {(job.assignedTechs || []).length === 0
            ? "Unassigned"
            : `Assigned: ${job.assignedTechs.map(staffName).join(", ")}`}
        </span>
      </div>
    );
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Work Orders</h2>

      {canCreate && (
        <>
          <button
            style={styles.button}
            onClick={() => setShowCreate((s) => !s)}
          >
            {showCreate ? "Hide New Work Order" : "New Work Order"}
          </button>
          {showCreate && (
            <div style={styles.card}>
              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{ display: "block", fontSize: "12px", color: "#666" }}
                >
                  Summary of work needed
                </label>
                <input
                  style={{ ...styles.input, margin: "2px 0 0", width: "100%" }}
                  placeholder="e.g. Replace burned-out hallway lights"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{ display: "block", fontSize: "12px", color: "#666" }}
                >
                  Details
                </label>
                <textarea
                  style={{
                    ...styles.textarea,
                    margin: "2px 0 0",
                    width: "100%",
                  }}
                  placeholder="Additional context, location, access notes, urgency, etc."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{ display: "block", fontSize: "12px", color: "#666" }}
                >
                  Building
                </label>
                <select
                  style={{ ...styles.input, margin: "2px 0 0" }}
                  value={buildingId}
                  onChange={(e) => {
                    setBuildingId(e.target.value);
                    setEquipmentIds([]);
                  }}
                >
                  <option value="">Select building</option>
                  {buildings.map(({ building }) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              {buildingId && (
                <div style={{ marginBottom: "10px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Equipment (check all that apply)
                  </label>
                  {equipmentForBuilding.length === 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        fontStyle: "italic",
                        margin: "4px 0",
                      }}
                    >
                      No equipment on file for this building.
                    </div>
                  )}
                  {equipmentForBuilding.map((eq) => (
                    <label
                      key={eq.id}
                      style={{ display: "block", fontSize: "13px" }}
                    >
                      <input
                        type="checkbox"
                        checked={equipmentIds.includes(eq.id)}
                        onChange={() =>
                          setEquipmentIds((p) =>
                            p.includes(eq.id)
                              ? p.filter((x) => x !== eq.id)
                              : [...p, eq.id]
                          )
                        }
                      />{" "}
                      {eq.type}
                      {eq.subType ? ` (${eq.subType})` : ""}
                    </label>
                  ))}
                  <label style={{ display: "block", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={otherChecked}
                      onChange={(e) => setOtherChecked(e.target.checked)}
                    />{" "}
                    Other (not equipment-specific — describe in Details above)
                  </label>
                </div>
              )}

              <button
                style={styles.primaryButton}
                onClick={handleCreate}
                disabled={!title || !buildingId}
              >
                Create Work Order
              </button>
            </div>
          )}
        </>
      )}

      {buildings.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          No buildings to show work orders for.
        </p>
      ) : (
        buildings.map(({ building }) => {
          const jobs = jobsRaw
            .filter((j) => j.locationId === building.id)
            // Once a work order is closed (completed) or canceled it
            // moves off the live board and into the Work Records page.
            .filter((j) => j.status !== "canceled" && j.status !== "closed")
            .sort(
              (a, b) =>
                STATUS_ORDER.indexOf(a.status || "unassigned") -
                STATUS_ORDER.indexOf(b.status || "unassigned")
            );
          const contractorJobs = publicJobsRaw
            .filter((j) => j.locationId === building.id)
            .filter((j) => j.status !== "canceled" && j.status !== "closed")
            .sort(
              (a, b) =>
                new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
            );
          return (
            <div key={building.id} style={{ marginTop: "14px" }}>
              <h3 style={{ marginBottom: "2px" }}>{building.name}</h3>
              {jobs.length === 0 ? (
                <p style={{ fontSize: "12px", color: "#666" }}>
                  No open work orders.
                </p>
              ) : (
                jobs.map(renderJob)
              )}
              {contractorJobs.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  <strong style={{ fontSize: "12px", color: "#666" }}>
                    From your contractor
                  </strong>
                  {contractorJobs.map(renderContractorJob)}
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );
}
